import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { sudoSessionEstablished } from '../store/authSlice';
import { authAPI } from '../services/api';

function SudoPrompt({ open, onClose }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [testSuccess, setTestSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const handleTest = async () => {
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setTesting(true);
    setError('');
    setTestSuccess(false);

    try {
      // Test credentials without creating a session (we'll create a test endpoint)
      // For now, we'll use the same endpoint but won't dispatch the action
      await authAPI.validateSudoPassword(username, password);
      setTestSuccess(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials');
      setTestSuccess(false);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!testSuccess) {
      setError('Please test credentials first before saving');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authAPI.validateSudoPassword(username, password);
      dispatch(sudoSessionEstablished());

      // Emit custom event for dashboard to listen to
      window.dispatchEvent(new CustomEvent('sudo-session-established'));

      setUsername('');
      setPassword('');
      setTestSuccess(false);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setError('');
    setTestSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
        Samba Domain Administrator Credentials
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Important: These are THREE different credential types:
          </Typography>
          <Typography variant="body2" component="div">
            1. <strong>Web UI Login</strong> - For accessing this web application<br />
            2. <strong>SSH Credentials</strong> - For connecting to the remote Samba server<br />
            3. <strong>Domain Admin Credentials</strong> - For executing samba-tool commands (this form)
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {testSuccess && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Credentials validated successfully! Click "Save" to store them.
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Domain Administrator Username"
          type="text"
          fullWidth
          variant="outlined"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setTestSuccess(false);
          }}
          disabled={loading || testing}
          required
          sx={{ mb: 2 }}
          helperText="Samba domain admin username (e.g., administrator) - used for samba-tool commands"
        />
        <TextField
          margin="dense"
          label="Domain Administrator Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          variant="outlined"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setTestSuccess(false);
          }}
          disabled={loading || testing}
          required
          helperText="Domain admin password - will be encrypted and stored securely"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading || testing}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleTest}
          variant="outlined"
          color="primary"
          disabled={loading || testing || !username || !password}
          sx={{ mr: 1 }}
        >
          {testing ? <CircularProgress size={24} /> : 'Test'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="success"
          disabled={loading || testing || !testSuccess}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SudoPrompt;
